import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const TOKEN_KEY = 'token';

/**
 * base64url decode that survives missing padding and multi-byte claims.
 * Plain atob() throws on both, which would silently sign the user out.
 */
const base64UrlDecode = (segment) => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '==='.slice((base64.length + 3) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const decode = (token) => {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return JSON.parse(base64UrlDecode(parts[1]));
  } catch (error) {
    return null;
  }
};

const authService = {
  login: async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/v1/auth/login`, {
        email,
        password,
      });
      return { success: true, data: response.data };
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Login failed',
          status: error.response.status,
        };
      }
      return {
        success: false,
        message: 'Network error. Please check your connection.',
        status: null,
      };
    }
  },

  /**
   * Register a new user.
   *  - FREE_TIER: account is created immediately; resolves with a JWT.
   *  - Paid tiers: resolves with a Stripe checkout URL in `data.checkout_url`.
   *
   * @param {string} email
   * @param {string} password
   * @param {string} subscriptionTier  'FREE_TIER' | 'BASIC' | 'PRO' | 'ELITE'
   * @param {string|null} sport        Required for paid tiers, omit for FREE_TIER
   */
  /**
   * Register a new account.
   *
   * @param {string} email
   * @param {string} password
   * @param {string} subscriptionTier   e.g. 'FREE_TIER'
   * @param {object} [options]
   * @param {string} [options.sport]              Optional sport identifier.
   * @param {boolean} [options.marketingConsent]  True if the user opted in to
   *   marketing / product-update emails. Sent as `marketing_consent`.
   *   See docs/register-marketing-consent.md.
   */
  register: async (email, password, subscriptionTier, options = {}) => {
    try {
      const { sport = null, marketingConsent = false } = options;

      const requestBody = {
        email,
        password,
        subscription_tier: subscriptionTier,
        marketing_consent: Boolean(marketingConsent),
      };
      if (sport) requestBody.sport = sport;

      const response = await axios.post(`${API_URL}/api/v1/auth/register`, requestBody);

      // Free tier returns the token directly.
      if (subscriptionTier === 'FREE_TIER' && response.data.token) {
        return {
          success: true,
          token: response.data.token,
          user: response.data.user,
          message: response.data.message,
        };
      }

      // Paid tiers return checkout data.
      return {
        success: true,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error) {
      if (error.response) {
        return {
          success: false,
          message: error.response.data?.message || 'Registration failed',
          error: error.response.data?.error,
          status: error.response.status,
        };
      }
      return {
        success: false,
        message: 'Network error. Please check your connection.',
        status: null,
      };
    }
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
  },

  getToken: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch (error) {
      return null;
    }
  },

  setToken: (token) => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch (error) {
      // Storage can be unavailable in private browsing; the session simply
      // won't survive a reload.
    }
  },

  /** True when a token exists and hasn't expired. */
  isTokenValid: () => {
    const payload = decode(authService.getToken());
    if (!payload || typeof payload.exp !== 'number') return false;
    return payload.exp > Date.now() / 1000;
  },

  /** Decoded payload of the stored token, or null if absent/malformed. */
  decodeToken: () => decode(authService.getToken()),
};

export default authService;
